import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import { ServiceType } from '../services/api';
import { FutureInstrument, StockInstrument } from '../types';
import { Portfolio } from './usePortfolios';
import { formatInt } from '../utils/numberFormat';

const coreService = apiService.getService(ServiceType.CORE);

interface UsePlacePairStrategyOptions {
    selectedFuture: FutureInstrument | null;
    selectedStocks: StockInstrument[];
    selectedPortfolio: Portfolio | undefined;
    selectedPortfolioId: string | null;
    closeType: string;
    takeProfitReturn: number;
    cutLossReturn: number;
    maxOpen: number;
    executionId: string | null;
    strategyId: string | null;
    onConfigUpdated?: (config: any) => void;
    /** v3: side opened when reference PnL < 0. SELL = short future + long stock. */
    activeSide?: 'BUY' | 'SELL';
    /** v3: user-entered futures reference price (index points). Required to start. */
    futureInstrumentPrice?: number | null;
    /** v3: pause the open trigger without stopping the worker. */
    isPause?: boolean;
    /** IMMEDIATE opens on start (no reference prices); REFERENCE waits for the trigger. */
    openMode?: 'IMMEDIATE' | 'REFERENCE';
    /** v3: auto force-close a pair held ≥ this many business days. 0 = disabled. */
    maxHoldingDays?: number;
    /** Deriv (DERIVATIVES) portfolio for the futures leg. */
    derivPortfolio?: Portfolio;
    /** Cash allocated to the stock leg (raw VND). */
    stockAllocatedCash?: number;
    /** Cash allocated to the deriv leg (raw VND collateral). */
    derivAllocatedCash?: number;
    /** Allocator balance mode. NOTIONAL_MATCH caps the two legs dollar-for-dollar;
     * MANUAL lets each leg trade independently. */
    balanceMode?: 'NOTIONAL_MATCH' | 'MANUAL';
    /** The saved pair being edited; settings GET/PATCH and place address it. */
    dataConfigId?: string | null;
    /** Save creates a NEW draft pair instead of reusing the portfolio's current one. */
    createNew?: boolean;
    /** Display name for a new pair. */
    pairName?: string;
}

export interface PlacePairResult {
    success: boolean;
    message: string;
    /** BE placePair response body (config-only path: started=false, dataConfigId, scheduleCreated). */
    data?: {
        date?: string;
        started?: boolean;
        dataConfigId?: string;
        /** true when this save created the default weekday 09:00 auto-start schedule. */
        scheduleCreated?: boolean;
        [key: string]: unknown;
    };
}

export const usePlacePairStrategy = ({
    selectedFuture,
    selectedStocks,
    selectedPortfolio,
    selectedPortfolioId,
    closeType,
    takeProfitReturn,
    cutLossReturn,
    maxOpen,
    executionId,
    strategyId,
    onConfigUpdated,
    activeSide,
    futureInstrumentPrice,
    isPause,
    openMode,
    maxHoldingDays,
    derivPortfolio,
    stockAllocatedCash,
    derivAllocatedCash,
    balanceMode,
    dataConfigId,
    createNew,
    pairName,
}: UsePlacePairStrategyOptions) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [placingPair, setPlacingPair] = useState<boolean>(false);
    const [placePairModalVisible, setPlacePairModalVisible] = useState<boolean>(false);
    const [placePairResult, setPlacePairResult] = useState<PlacePairResult | null>(null);
    const [savingSettings, setSavingSettings] = useState<boolean>(false);

    const handlePlacePairStrategy = useCallback(async () => {
        if (!selectedFuture || selectedStocks.length === 0) {
            message.warning('Please select a future and at least one stock');
            return;
        }

        if (!selectedPortfolio || !selectedPortfolio.portfolioId) {
            message.warning('Please select a portfolio');
            return;
        }

        // Validate stock proportions. v3 wire shape per symbol is the tuple
        // [ratio, reference_price, flash_volume_limit?]. reference_price may be
        // null at Save time (config-only); the BE start-guard enforces it
        // before the bot launches.
        const stockProportionObj: { [key: string]: (number | null)[] } = {};
        let hasInvalidProportion = false;

        selectedStocks.forEach(stock => {
            if (!stock.proportion || stock.proportion <= 0) {
                hasInvalidProportion = true;
            } else {
                // Bare ticker: the BE resolves the listing exchange (HNX/HSX/UPCOM)
                // from the price feed. Stamping HSX: here mislabelled HNX stocks.
                stockProportionObj[stock.symbol] = [
                    stock.proportion,
                    stock.referencePrice ?? null,
                    stock.flashVolumeLimit ?? null,
                ];
            }
        });

        if (hasInvalidProportion) {
            message.warning('Please set a weight for every selected stock');
            return;
        }

        setPlacingPair(true);
        setPlacePairModalVisible(true);

        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                message.error('Please login first');
                setPlacingPair(false);
                setPlacePairModalVisible(false);
                return;
            }

            // Get today's date in YYYY-MM-DD format
            const today = new Date();
            const dateStr = today.toISOString().split('T')[0];

            // Option B: cash inputs no longer mandatory at place time. placePair
            // with `allocatedCash: 0` on both legs takes the config-only fast
            // path on the BE (persist DC+IC, skip publish/execution). The user
            // funds each leg via the Deposit modal and then clicks Start. We
            // still require a derivatives portfolio so the deriv-leg IC row can
            // be created — without it the row would have no portfolio link and
            // future deposits couldn't route.
            if (derivPortfolio?.id == null) {
                message.warning('Pick a derivatives portfolio before saving the pair config.');
                setPlacingPair(false);
                setPlacePairModalVisible(false);
                return;
            }
            const stockCashOut = stockAllocatedCash ?? 0;
            const derivCashOut = derivAllocatedCash ?? 0;

            // futures_exchange comes from the future's symbol prefix when present
            // (e.g. "HNXDS:VN30F2605"), else defaults to HNXDS for VN30F.
            const futuresExchange = selectedFuture.symbol?.includes(':')
                ? selectedFuture.symbol.split(':')[0]
                : 'HNXDS';
            const futuresSymbol = selectedFuture.symbol?.includes(':')
                ? selectedFuture.symbol.split(':')[1]
                : selectedFuture.symbol;

            const requestBody: any = {
                strategy_type: 'pair',
                close_type: closeType,
                take_profit_return: takeProfitReturn,
                cut_loss_return: cutLossReturn,
                // Option B: Save Config NEVER starts the bot. is_starting=false
                // forces the BE config-only path (persist DC+IC, no publish, no
                // execution). Launching is a separate, explicit step via the
                // Start button → POST /pair/:dc/start.
                is_starting: false,
                max_open: maxOpen,
                active_side: activeSide ?? 'SELL',
                future_instrument_price: futureInstrumentPrice ?? null,
                is_pause: isPause ?? false,
                open_mode: openMode ?? 'REFERENCE',
                max_holding_days: maxHoldingDays ?? 0,
                stock_proportion: stockProportionObj,
                date: dateStr,
                stockLeg: {
                    portfolioId: selectedPortfolio.id,
                    allocatedCash: stockCashOut,
                },
                derivLeg: {
                    portfolioId: derivPortfolio.id,
                    allocatedCash: derivCashOut,
                    futures_exchange: futuresExchange,
                    futures_symbol: futuresSymbol,
                },
                balance_mode: balanceMode ?? 'NOTIONAL_MATCH',
                ...(createNew
                    ? { create_new: true, ...(pairName?.trim() ? { name: pairName.trim() } : {}) }
                    : dataConfigId ? { data_config_id: dataConfigId } : {}),
            };

            let response;
            let effectiveExecutionId = executionId;

            // If we have executionId, try to fetch existing pair config by executionId
            let configToUpdate = null;
            if (effectiveExecutionId && !createNew) {
                try {
                    const configResponse = await coreService.get(`/pair/settings/${selectedPortfolioId}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json'
                        },
                        params: dataConfigId ? { dataConfigId } : undefined,
                    });
                    if (configResponse?.data?.pid) {
                        configToUpdate = configResponse.data;
                    }
                } catch (error: any) {
                    // No existing config found for this execution - that's okay, we'll create a new one
                    if (error.response?.status !== 404) {
                        console.error('Error fetching pair config by execution:', error);
                    }
                }
            }

            // If existing config exists, use PATCH to update, otherwise POST to create
            if (configToUpdate && configToUpdate.pid) {
                // Update existing configuration
                const updatedRequestBody = {
                    settings: {
                        close_type: requestBody.close_type,
                        take_profit_return: requestBody.take_profit_return,
                        cut_loss_return: requestBody.cut_loss_return,
                        max_open: requestBody.max_open,
                        active_side: requestBody.active_side,
                        future_instrument_price: requestBody.future_instrument_price,
                        is_pause: requestBody.is_pause,
                        max_holding_days: requestBody.max_holding_days,
                        stock_proportion: requestBody.stock_proportion,
                    },
                    portfolio: selectedPortfolioId,
                    // the version read just above: a change made elsewhere since is a 409
                    expectedUpdatedAt: configToUpdate?.updatedAt,
                    ...(dataConfigId ? { data_config_id: dataConfigId } : {}),
                };
                response = await coreService.patch('/pair/settings/', updatedRequestBody, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });
            } else {
                // Place the pair configuration
                response = await coreService.post(`/pair/place/${selectedPortfolioId}`, requestBody, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });
            }

            // Update location state with executionId so subsequent operations will update instead of create
            if (effectiveExecutionId) {
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        ...location.state,
                        executionId: effectiveExecutionId,
                        strategyId: strategyId,
                        portfolioId: selectedPortfolioId,
                        portfolio: selectedPortfolio,
                        strategy: location.state?.strategy
                    }
                });
            }

            // Update config via callback if provided
            if (onConfigUpdated) {
                onConfigUpdated(response.data);
            }

            setPlacePairResult({
                success: true,
                message: configToUpdate
                    ? (response.data.message || 'Pair configuration updated successfully')
                    : (response.data.message || 'Pair placed successfully'),
                // The result modal reads `.date` / `.scheduleCreated` from here.
                data: response.data,
            });
        } catch (error: any) {
            console.error('Error placing pair:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to place pair strategy';
            setPlacePairResult({
                success: false,
                message: errorMessage
            });
        } finally {
            setPlacingPair(false);
        }
    }, [
        selectedFuture,
        selectedStocks,
        selectedPortfolio,
        selectedPortfolioId,
        closeType,
        takeProfitReturn,
        cutLossReturn,
        maxOpen,
            activeSide,
        futureInstrumentPrice,
        isPause,
        openMode,
        maxHoldingDays,
        executionId,
        strategyId,
        navigate,
        location,
        onConfigUpdated,
        derivPortfolio,
        stockAllocatedCash,
        derivAllocatedCash,
        balanceMode,
        dataConfigId,
        createNew,
        pairName,
    ]);

    // PATCH-only save. Writes the same settings the bot would consume, but
    // skips the RabbitMQ/bot-ack roundtrip — useful when the user wants to tweak
    // close_type / TP / SL / proportions without re-triggering placement (and
    // without sitting through the 60s ack wait if the bot is offline).
    // Resolves `true` only when the PATCH succeeded, so "Update & Start" can
    // refuse to launch the bot on a config that was never persisted.
    const handleSavePairSettings = useCallback(async (): Promise<boolean> => {
        if (!selectedPortfolioId) {
            message.warning('Please select a portfolio');
            return false;
        }
        // An empty basket is a legitimate update: the user is clearing the stock leg
        // to reseed it later. The BE closes the symbol rows and parks their capital
        // in `stock_unallocated_cash`, which is folded back in when symbols return.
        // (It still refuses if the bot is running or any leg holds a position.)

        const stockProportionObj: { [key: string]: (number | null)[] } = {};
        let hasInvalidProportion = false;
        selectedStocks.forEach(stock => {
            if (!stock.proportion || stock.proportion <= 0) {
                hasInvalidProportion = true;
            } else {
                // Bare ticker: the BE resolves the listing exchange (HNX/HSX/UPCOM)
                // from the price feed. Stamping HSX: here mislabelled HNX stocks.
                stockProportionObj[stock.symbol] = [
                    stock.proportion,
                    stock.referencePrice ?? null,
                    stock.flashVolumeLimit ?? null,
                ];
            }
        });
        if (hasInvalidProportion) {
            message.warning('Please set a weight for every selected stock');
            return false;
        }

        setSavingSettings(true);
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                message.error('Please login first');
                return false;
            }

            const updatedRequestBody = {
                settings: {
                    close_type: closeType,
                    take_profit_return: takeProfitReturn,
                    cut_loss_return: cutLossReturn,
                    max_open: maxOpen,
                        active_side: activeSide ?? 'SELL',
                    future_instrument_price: futureInstrumentPrice ?? null,
                    is_pause: isPause ?? false,
                    open_mode: openMode ?? 'REFERENCE',
                    max_holding_days: maxHoldingDays ?? 0,
                    stock_proportion: stockProportionObj,
                    // Dual-leg allocation knobs — persisted for UX (so the form
                    // remembers them on reload). Actual mid-session rebalancing
                    // still has to go through Place / placement service.
                    deriv_portfolio_id: derivPortfolio?.id ?? null,
                    stock_allocated_cash: stockAllocatedCash ?? null,
                    deriv_allocated_cash: derivAllocatedCash ?? null,
                    balance_mode: balanceMode ?? 'NOTIONAL_MATCH',
                },
                portfolio: selectedPortfolioId,
                ...(dataConfigId ? { data_config_id: dataConfigId } : {}),
            };

            const response = await coreService.patch('/pair/settings/', updatedRequestBody, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });

            if (onConfigUpdated) {
                onConfigUpdated(response.data);
            }

            // Emptying the stock basket hands its idle cash back to the account;
            // the BE remembers the amount and replays it into the rebuilt basket.
            // Say so explicitly — money moved, and a shortfall means part of it
            // had already been spent elsewhere.
            const restored = response.data?.restoredStockCash;
            if (restored && restored.restored > 0) {
                message.success(
                    restored.shortfall > 0
                        ? `Settings saved — restored ${formatInt(restored.restored)} of ${formatInt(restored.remembered)} VND released earlier (${formatInt(restored.shortfall)} no longer available).`
                        : `Settings saved — restored ${formatInt(restored.restored)} VND released earlier.`,
                    6,
                );
            } else if (restored && restored.restored === 0) {
                message.warning(
                    `Settings saved, but the ${formatInt(restored.remembered)} VND released earlier is no longer available on the account — deposit manually.`,
                    8,
                );
            } else if (response.data?.restoreError) {
                message.warning(`Settings saved. Restoring released cash failed: ${response.data.restoreError}`, 8);
            } else {
                message.success(response.data?.message || 'Settings saved');
            }
            return true;
        } catch (error: any) {
            console.error('Error saving pair settings:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save settings';
            message.error(errorMessage);
            return false;
        } finally {
            setSavingSettings(false);
        }
    }, [
        selectedPortfolioId,
        selectedStocks,
        closeType,
        takeProfitReturn,
        cutLossReturn,
        maxOpen,
            activeSide,
        futureInstrumentPrice,
        isPause,
        openMode,
        maxHoldingDays,
        derivPortfolio,
        stockAllocatedCash,
        derivAllocatedCash,
        balanceMode,
        onConfigUpdated,
        dataConfigId,
    ]);

    const handleClosePlacePairModal = useCallback(() => {
        setPlacePairModalVisible(false);
        setPlacePairResult(null);
    }, []);

    return {
        placingPair,
        placePairModalVisible,
        placePairResult,
        handlePlacePairStrategy,
        handleClosePlacePairModal,
        savingSettings,
        handleSavePairSettings,
    };
};



